import { Button, Text } from '@chakra-ui/react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { SYSTEM_USER_ID } from 'app/constants/reconciliations';

import { SalaryReconciliationDiscrepancy } from 'app/types/salaryReconciliations';

import { formatDateTime, formatReconciliationAmount } from 'app/utils/reconciliations';
import { formatDiscrepancyReason, renderFixability } from 'app/utils/salaryReconciliations';

interface SalaryDiscrepancyColumnsOptions {
  showCompanyColumn?: boolean;
  showResolvedAt?: boolean;
  showResolvedBy?: boolean;
  onResolve?: (discrepancy: SalaryReconciliationDiscrepancy) => void;
}

/**
 * @description Names who fixed a discrepancy by hand: the admin's full name, "System" for the system user, or their id
 * when the name can't be found.
 */
const describeResolver = ({ resolvedBy, resolvedByName }: SalaryReconciliationDiscrepancy) => {
  if (resolvedByName) {
    return resolvedByName;
  }
  if (resolvedBy === SYSTEM_USER_ID) {
    return 'System';
  }
  return resolvedBy || '-';
};

/**
 * @description Columns of a salary accrual reconciliation run's discrepancies in one view. The company column is left
 * out in a company's own view, the resolved time only shown for resolved ones, who fixed one by hand and why only for
 * those, and the Resolve action only when it is given, on discrepancies still open.
 */
function getSalaryDiscrepancyColumns({
  showCompanyColumn = true,
  showResolvedAt = false,
  showResolvedBy = false,
  onResolve = undefined,
}: SalaryDiscrepancyColumnsOptions = {}) {
  const columnHelper = createColumnHelper<SalaryReconciliationDiscrepancy>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<SalaryReconciliationDiscrepancy, any>[] = [
    columnHelper.accessor('companyName', {
      id: 'company',
      header: 'Company',
      enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('individualName', {
      header: 'Person',
      enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('externalPayrollId', {
      header: 'Payroll ID',
      enableSorting: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('reason', {
      header: 'Reason',
      enableSorting: false,
      cell: (info) => (
        <Text minW="200px" whiteSpace="normal">
          {formatDiscrepancyReason(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('expectedAmount', {
      header: 'Expected',
      enableSorting: false,
      cell: (info) => formatReconciliationAmount(info.getValue()),
    }),
    columnHelper.accessor('actualAmount', {
      header: 'Actual',
      enableSorting: false,
      cell: (info) => formatReconciliationAmount(info.getValue()),
    }),
    columnHelper.accessor('difference', {
      header: 'Difference',
      enableSorting: false,
      cell: (info) => formatReconciliationAmount(info.getValue()),
    }),
    columnHelper.accessor('fixability', {
      header: 'Would an adjustment fix it',
      enableSorting: false,
      cell: (info) => renderFixability(info.getValue()),
    }),
    columnHelper.accessor('firstSeenAt', {
      header: 'First seen',
      enableSorting: false,
      cell: (info) => formatDateTime(info.getValue()),
    }),
    columnHelper.accessor('resolvedAt', {
      id: 'resolvedAt',
      header: 'Resolved',
      enableSorting: false,
      cell: (info) => formatDateTime(info.getValue()),
    }),
    columnHelper.accessor('resolvedByName', {
      id: 'resolvedBy',
      header: 'Fixed by',
      enableSorting: false,
      cell: (info) => describeResolver(info.row.original),
    }),
    columnHelper.accessor('resolutionNote', {
      id: 'resolutionNote',
      header: 'Note',
      enableSorting: false,
      cell: (info) => (
        <Text maxW="360px" whiteSpace="normal">
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) =>
        info.row.original.resolvedAt ? null : (
          <Button
            size="xs"
            variant="outline"
            onClick={(event) => {
              event.stopPropagation();
              onResolve?.(info.row.original);
            }}
            data-testid={`resolve-${info.row.original.id}`}
          >
            Resolve
          </Button>
        ),
    }),
  ];

  return columns.filter(
    (column) =>
      (showCompanyColumn || column.id !== 'company') &&
      (showResolvedAt || column.id !== 'resolvedAt') &&
      (showResolvedBy || (column.id !== 'resolvedBy' && column.id !== 'resolutionNote')) &&
      (onResolve !== undefined || column.id !== 'actions')
  );
}

export default getSalaryDiscrepancyColumns;
